import React from 'react';

import AvatarBlock from '@wp-block-builder/blocks/Avatar';
import ButtonBlock from '@wp-block-builder/blocks/Button';
import { ReadOnlyBlock as ColumnsSection } from '@wp-block-builder/blocks/ColumnsSection';
import { ContainerBlock } from '@wp-block-builder/blocks/Container/ContainerBlock';
import ContainerYieldBlock from '@wp-block-builder/blocks/ContainerYield';
import DividerBlock from '@wp-block-builder/blocks/Divider';
import HeadingBlock from '@wp-block-builder/blocks/Heading';
import HtmlBlock from '@wp-block-builder/blocks/Html';
import ImageBlock from '@wp-block-builder/blocks/Image';
import getLoopRawLiquidBlock from '@wp-block-builder/blocks/LoopContainer/getLoopRawLiquidBlock';
import { LoopContainerBlock } from '@wp-block-builder/blocks/LoopContainer/LoopContainerBlock';
import ProgressBarBlock from '@wp-block-builder/blocks/ProgressBar';
import SpacerBlock from '@wp-block-builder/blocks/Spacer';
import TextBlock from '@wp-block-builder/blocks/Text';
import buildBlockComponent from '@wp-block-builder/email-builder/buildBlockComponent';
import { type TBlock, type TTemplate } from '@wp-block-builder/package';
import { useGlobalConfiguration } from '@wp-block-builder/stores/WaypointDocumentStore/useGlobalStyles';
import { useSkipLiquid } from '@wp-block-builder/stores/WaypointRendererStore';

import BlockSectionWrapper from '../../document-layout/BlockSectionWrapper';
import { useEvaluatedHiddenCondition, useSectionStyle } from '../../document-layout/hooks';

type Props = {
  block: TBlock;
};
export default function ReadOnlySection({ block }: Props) {
  const skipLiquid = useSkipLiquid();
  if (skipLiquid) {
    return <RawLiquidBlockComponent block={block} />;
  }
  return <ReadOnlyBlockComponent block={block} />;
}

type TBlockSectionProps = {
  block: TBlock;
  children: React.ReactNode;
};
function BaseReadOnlySection({ block, children }: TBlockSectionProps) {
  const hidden = useEvaluatedHiddenCondition(block);
  const style = useSectionStyle(block);

  if (hidden) {
    return null;
  }
  return <BlockSectionWrapper style={style}>{children}</BlockSectionWrapper>;
}

const ReadOnlyBlockComponent = buildBlockComponent({
  Avatar: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <AvatarBlock block={block} skipLiquid={false} />
    </BaseReadOnlySection>
  ),
  Button: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <ButtonBlock block={block} skipLiquid={false} />
    </BaseReadOnlySection>
  ),
  ColumnsSection: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <ColumnsSection block={block} BlockComponent={ReadOnlyBlockComponent} />
    </BaseReadOnlySection>
  ),
  Container: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <ContainerBlock block={block} BlockComponent={ReadOnlyBlockComponent} />
    </BaseReadOnlySection>
  ),
  ContainerYield: ({ block }) => (
    <div>
      <ContainerYieldBlock block={block} />
    </div>
  ),
  Divider: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <DividerBlock block={block} />
    </BaseReadOnlySection>
  ),
  Heading: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <HeadingBlock block={block} skipLiquid={false} />
    </BaseReadOnlySection>
  ),
  Html: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <HtmlBlock block={block} skipLiquid={false} />
    </BaseReadOnlySection>
  ),
  Image: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <ImageBlock block={block} skipLiquid={false} />
    </BaseReadOnlySection>
  ),
  LoopContainer: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <LoopContainerBlock block={block} BlockComponent={ReadOnlyBlockComponent} />
    </BaseReadOnlySection>
  ),
  ProgressBar: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <ProgressBarBlock block={block} />
    </BaseReadOnlySection>
  ),
  Spacer: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <SpacerBlock block={block} />
    </BaseReadOnlySection>
  ),
  Text: ({ block }) => (
    <BaseReadOnlySection block={block}>
      <TextBlock block={block} />
    </BaseReadOnlySection>
  ),
});

type RawLiquidBlockProps =
  | { block: TBlock; renderChildren: (template: TTemplate) => string; children?: never }
  | { block: TBlock; renderChildren?: never; children: JSX.Element };
function RawLiquidBlock({ block, renderChildren, children }: RawLiquidBlockProps) {
  const configuration = useGlobalConfiguration();
  const style = useSectionStyle(block);
  const commonProps: { 'data-hidden-condition'?: string } = {};
  if (block.sectionProps?.hiddenCondition) {
    commonProps['data-hidden-condition'] = block.sectionProps.hiddenCondition;
  }
  if (renderChildren) {
    return <div {...commonProps} style={style} dangerouslySetInnerHTML={{ __html: renderChildren(configuration) }} />;
  }
  return (
    <div {...commonProps} style={style}>
      {children}
    </div>
  );
}

const RawLiquidBlockComponent = buildBlockComponent({
  Avatar: ({ block }) => (
    <RawLiquidBlock block={block}>
      <AvatarBlock block={block} skipLiquid />
    </RawLiquidBlock>
  ),
  Button: ({ block }) => (
    <RawLiquidBlock block={block}>
      <ButtonBlock block={block} skipLiquid />
    </RawLiquidBlock>
  ),
  ColumnsSection: ({ block }) => (
    <RawLiquidBlock block={block}>
      <ColumnsSection block={block} BlockComponent={RawLiquidBlockComponent} />
    </RawLiquidBlock>
  ),
  Container: ({ block }) => (
    <RawLiquidBlock block={block}>
      <ContainerBlock block={block} BlockComponent={RawLiquidBlockComponent} />
    </RawLiquidBlock>
  ),
  ContainerYield: ({ block }) => <ContainerYieldBlock block={block} />,
  Divider: ({ block }) => (
    <RawLiquidBlock block={block}>
      <DividerBlock block={block} />
    </RawLiquidBlock>
  ),
  Heading: ({ block }) => (
    <RawLiquidBlock block={block}>
      <HeadingBlock block={block} skipLiquid />
    </RawLiquidBlock>
  ),
  Html: ({ block }) => (
    <RawLiquidBlock block={block}>
      <HtmlBlock block={block} skipLiquid />
    </RawLiquidBlock>
  ),
  Image: ({ block }) => (
    <RawLiquidBlock block={block}>
      <ImageBlock block={block} skipLiquid />
    </RawLiquidBlock>
  ),
  LoopContainer: ({ block }) => (
    <RawLiquidBlock
      block={block}
      renderChildren={(c) =>
        getLoopRawLiquidBlock({
          block,
          configuration: c,
          BlockComponent: RawLiquidBlockComponent,
        })
      }
    />
  ),
  ProgressBar: ({ block }) => (
    <RawLiquidBlock block={block}>
      <ProgressBarBlock block={block} />
    </RawLiquidBlock>
  ),
  Spacer: ({ block }) => (
    <RawLiquidBlock block={block}>
      <SpacerBlock block={block} />
    </RawLiquidBlock>
  ),
  Text: ({ block }) => (
    <RawLiquidBlock block={block}>
      <TextBlock block={block} />
    </RawLiquidBlock>
  ),
});
